import React from 'react';
import {
  stepClasses,
  StepConnector,
  stepConnectorClasses,
  StepLabel,
  stepLabelClasses,
  type StepLabelProps,
  Stepper,
  type StepperProps,
  styled,
  useStepContext
} from '@mui/material';
import { mergeSx } from 'merge-sx';

import StepIcon from '@/assets/icons/step-circle.svg?react';
import StepCompletedIcon from '@/assets/icons/step-circle-completed.svg?react';
import palette from '@/theme/palette';

export const VerticalStepper = styled((props: StepperProps) => (
  <Stepper {...props} connector={<VerticalStepConnector />} orientation="vertical" />
))({});

const VerticalStepConnector = styled(StepConnector)(({ theme: { palette, spacing } }) => ({
  width: spacing(2.5),
  margin: 0,

  [`& .${stepConnectorClasses.lineVertical}`]: {
    margin: '0 auto',
    width: 1,
    minHeight: spacing(5),
    borderColor: palette.grey[500]
  },

  [`&.${stepConnectorClasses.active} .${stepConnectorClasses.line},
    &.${stepConnectorClasses.completed} .${stepConnectorClasses.line}`]: {
    borderColor: palette.primary.main
  }
}));

export const VerticalStepLabel = styled((props: StepLabelProps) => {
  const step = useStepContext();

  if (!('index' in step)) throw new Error('No context for step is defined');

  return (
    <StepLabel
      {...props}
      sx={mergeSx(
        {
          alignItems: 'flex-start',

          // vertical connection line inside label content
          [`.${stepClasses.vertical}:not(:last-child) &`]: {
            backgroundImage: `linear-gradient(to right, ${palette.grey[500]} 0, ${palette.grey[500]} 100%);`,
            backgroundSize: '1px calc(100% - 20px)',
            backgroundRepeat: 'no-repeat',
            backgroundPositionX: '9.5px',
            backgroundPositionY: '20px'
          },

          [`.${stepLabelClasses.completed}.${stepClasses.vertical}:not(:last-child) &`]: {
            backgroundImage: `linear-gradient(to right, ${palette.primary.main} 0, ${palette.primary.main} 100%);`
          }
        },
        props.sx
      )}
      StepIconProps={{
        icon:
          step.completed && !step.active ? (
            <StepCompletedIcon width={16} height={16} />
          ) : (
            <StepIcon width={16} height={16} />
          )
      }}
    />
  );
})(({ theme: { palette, typography, spacing } }) => ({
  padding: 0,

  [`& .${stepLabelClasses.iconContainer}`]: {
    padding: spacing(0.25),
    marginInlineEnd: spacing(1)
  },

  [`& .${stepLabelClasses.label}`]: {
    ...typography.body2
  },

  [`& .${stepLabelClasses.iconContainer},
    & .${stepLabelClasses.label},
    & .${stepLabelClasses.label}.${stepLabelClasses.completed}`]: {
    color: palette.text.disabled
  },

  [`& .${stepLabelClasses.iconContainer}.${stepLabelClasses.active},
    & .${stepLabelClasses.iconContainer}.${stepLabelClasses.completed},
    & .${stepLabelClasses.label}.${stepLabelClasses.active}`]: {
    color: palette.primary.main
  }
}));
